import $ from 'jquery';
import camelCase from 'lodash.camelcase';
import * as React from 'react';

import routes from 'defs/routes';
import Sequences from 'defs/sequences';

import { DISPLAY_FORMATS, format, PARSE_FORMATS } from 'helper/dateTime';
import i18n from 'helper/i18n';
import { template, toString } from 'helper/string';

import { DateTimeType } from 'types/DateTime';
import { HistoryType } from 'types/withRouter';

import BoardTrendChooser from 'containers/molecules/Dashboard/BoardTrendChooser';
import BoardTrendSlider from 'containers/molecules/Dashboard/BoardTrendSlider';
import FameBadge from 'containers/molecules/Fame/Badge/FameBadge';

import InfoLink from 'components/atoms/InfoLink';
import InfoSummary from 'components/atoms/InfoSummary';
import Link from 'components/atoms/Link';
import Loader from 'components/atoms/Loader';
import ProgressRadial from 'components/atoms/ProgressRadial';
import Bevel from 'components/helper/Bevel';
import withSequenceState from 'components/hoc/withSequenceState';
import BoardTrendButton from 'components/molecules/Board/BoardTrendButton';
import { BadgeType } from 'components/molecules/Fame/ProgressWrapper';
import { HealthGoalTypes, HealthGoalTypesType } from 'components/molecules/HealthGoalSelection';
import Tiles, { Cell, Tile } from 'components/molecules/Tiles';

export type AwardBadgeProps = { label?: string; key: string };

export type BoardTrendProps = {
	rangeDiff: number;
	currentWeight: number;
	desiredWeight: number;
	weightReduceGoal: number;
	healthGoal: HealthGoalTypesType;
	initialWeightLost: number;
	weightLost: number;
	badges: BadgeType[];
	totalBadges?: number;
	anamnesisTimestamp?: DateTimeType;
	isSequenceLoading: boolean;
	history: HistoryType;
	doSaveScrollPosition: (position: number) => void;
};

const getAwardBadgeProps = (badge): AwardBadgeProps => {
	const key = badge ? `${badge.id}-${badge.level}` : 'empty$badge';

	const label = badge
		? i18n(`react.impulsCoach.events.badge.${camelCase(badge.code)}.${camelCase(badge.eventLevel)}.title`)
		: void 0;

	return {
		label,
		key,
	};
};

const renderTargetMessage = () => (
	<div className="m-board-trend__target">
		<p className="m-board-trend__target-text">{i18n('react.impulsCoach.molecules.BoardTrend.weight.goal.label')}</p>
		<div className="m-board-trend__link">
			<Link
				modifier="button"
				title={i18n('react.impulsCoach.molecules.BoardTrend.weight.goal.link')}
				href={routes.anamnesis.indexRoute.href}
				iconRight="link-arrow"
			/>
		</div>
	</div>
);

const UICellModifiers = {
	borderless: ['m-tiles__cell--borderless'],
};

const renderSinceDateLabel = (date?: string): string => {
	if (!date) {
		return '';
	}
	return template(
		i18n(`react.impulsCoach.molecules.BoardTrend.weight.since`),
		{
			date: format(date, DISPLAY_FORMATS.DATE, PARSE_FORMATS.DATE),
		},
		true
	);
};

const BoardTrendBadges = (props: BoardTrendProps) => {
	let firstBadgeRow;
	let secondBadgeRow;

	if (props.badges && Array.isArray(props.badges) && props.badges.length > 0) {
		firstBadgeRow = [props.badges[0] ? props.badges[0] : null, props.badges[1] ? props.badges[1] : null];
		secondBadgeRow = [props.badges[2] ? props.badges[2] : null];
	} else {
		firstBadgeRow = [null, null];
		secondBadgeRow = [null];
	}

	return (
		<div className="row">
			<div className="col-xs-12">
				{props.badges && Array.isArray(props.badges) && props.badges.length > 0 && (
					<Tiles title={i18n('react.impulsCoach.molecules.BoardTrend.awards.sectionTitle')}>
						<Tile line>
							{firstBadgeRow.map((badge, id) => {
								const label = getAwardBadgeProps(badge).label;

								return (
									<Cell key={id} label={label}>
										<FameBadge award={badge} showNotification={false} />
									</Cell>
								);
							})}
						</Tile>
						<Tile>
							{secondBadgeRow.map((badge, id) => {
								const label = getAwardBadgeProps(badge).label;

								return (
									<Cell key={id} label={label}>
										<FameBadge award={badge} showNotification={false} />
									</Cell>
								);
							})}
							<Cell modifiers={UICellModifiers.borderless}>
								<InfoLink
									title={props.totalBadges || ''}
									text={i18n(`react.impulsCoach.molecules.BoardTrend.badges.total`)}
									href={routes.mySuccess.indexRoute.href}
								/>
							</Cell>
						</Tile>
					</Tiles>
				)}
			</div>
		</div>
	);
};

export const BoardTrend = (props: BoardTrendProps) => {
	let weightLostPercentage = 0;

	if (Math.abs(props.weightLost) > 0) {
		weightLostPercentage = (props.weightLost / props.weightReduceGoal) * 100;
	}

	if (props.weightLost >= props.weightReduceGoal) {
		weightLostPercentage = 100;
	}

	const isUpdating = props.isSequenceLoading;

	return (
		<div className={`m-board-trend${isUpdating ? ' m-board-trend--loading' : ''}`}>
			<div className="container container--full-width-ms">
				<div className="row">
					<div className="col-xs-12">
						<div className="m-board-trend__wrapper">
							<h2 className="m-board-trend__title">
								{i18n('react.impulsCoach.molecules.BoardTrend.title')}
							</h2>

							{!props.currentWeight && <Loader />}
							{props.currentWeight && <BoardTrendChooser />}
							{props.currentWeight && (
								<div className="m-board-trend__50p">
									<BoardTrendSlider />
									<BoardTrendButton
										onClick={() => {
											const offset = $(document).scrollTop()!;
											props.doSaveScrollPosition(offset);
										}}
										history={props.history}
									/>
								</div>
							)}

							{props.currentWeight && (
								<div className="m-board-trend__tiles">
									<Tiles>
										<Tile>
											{props.healthGoal === HealthGoalTypes.loseWeight ? (
												<Cell
													label={i18n('react.impulsCoach.molecules.BoardTrend.weight.goal')}
													info={i18n('react.impulsCoach.molecules.BoardTrend.weight.current')}
												>
													<InfoSummary
														title={toString(props.currentWeight)}
														text={i18n(
															'react.impulsCoach.molecules.BoardTrend.weight.unit'
														)}
														extra={{
															title: `${props.desiredWeight}`,
															text: i18n(
																'react.impulsCoach.molecules.BoardTrend.weight.unit'
															),
														}}
													/>
												</Cell>
											) : (
												<Cell
													label={i18n(
														'react.impulsCoach.molecules.BoardTrend.weight.current'
													)}
													info={''}
												>
													<InfoSummary
														title={toString(props.currentWeight)}
														text={i18n(
															'react.impulsCoach.molecules.BoardTrend.weight.unit'
														)}
													/>
												</Cell>
											)}

											{props.healthGoal === HealthGoalTypes.loseWeight ? (
												<Cell
													label={
														props.weightLost < 0
															? i18n(
																	'react.impulsCoach.molecules.BoardTrend.weight.gained'
															  )
															: i18n('react.impulsCoach.molecules.BoardTrend.weight.lost')
													}
												>
													<ProgressRadial
														reach={Math.abs(props.weightLost)}
														total={props.weightReduceGoal}
														text={i18n(
															'react.impulsCoach.molecules.BoardTrend.weight.unit'
														)}
														percent={weightLostPercentage}
														meterColor="017580"
														modifiers={['weight']}
													/>
												</Cell>
											) : (
												<Cell
													label={
														props.initialWeightLost < 0
															? i18n(
																	'react.impulsCoach.molecules.BoardTrend.weight.gained'
															  )
															: i18n('react.impulsCoach.molecules.BoardTrend.weight.lost')
													}
													text={renderSinceDateLabel(
														(props.anamnesisTimestamp || ({} as any)).date
													)}
												>
													<InfoSummary
														title={toString(Math.abs(props.initialWeightLost))}
														text={i18n(
															'react.impulsCoach.molecules.BoardTrend.weight.unit'
														)}
														image={
															props.initialWeightLost === 0
																? '' // nothing lost, nothing gained === no icon
																: props.initialWeightLost < 0
																? 'arrow-up'
																: 'arrow-down'
														}
													/>
												</Cell>
											)}
										</Tile>
									</Tiles>
								</div>
							)}

							{props.healthGoal === HealthGoalTypes.loseWeight &&
							props.weightLost >= props.weightReduceGoal
								? renderTargetMessage()
								: null}

							{isUpdating && <Loader hasContainer={false} modifier="white" />}
						</div>
					</div>
				</div>
				<BoardTrendBadges {...props} />
			</div>
			<Bevel modifier="bottom-left" color="ffffff" />
		</div>
	);
};

BoardTrend.displayName = 'BoardTrend';

export default withSequenceState(BoardTrend, [Sequences.DASHBOARD_MEASUREMENT_UPDATING]);
